import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import CreateMeetingModal from './components/CreateMeetingModal';
import MeetingDetailsModal from './components/MeetingDetailsModal';

const PCFAdminMeetingsPortal = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);
  const [pastMeetings, setPastMeetings] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    if (user) {
      fetchMeetings();
    }
  }, [user]);

  const fetchMeetings = async () => {
    try {
      setLoading(true);
      const now = new Date();
      const today = now?.toISOString()?.split('T')?.[0];
      const currentTime = now?.toTimeString()?.split(' ')?.[0]?.slice(0, 5);

      // Fetch upcoming meetings
      const { data: upcoming, error: upcomingError } = await supabase
        ?.from('meetings')
        ?.select(`
          *,
          organizer:user_profiles!created_by(full_name, email),
          team:teams(team_name)
        `)
        ?.eq('status', 'scheduled')
        ?.or(`meeting_date.gt.${today},and(meeting_date.eq.${today},meeting_time.gte.${currentTime})`)
        ?.order('meeting_date', { ascending: true })
        ?.order('meeting_time', { ascending: true });

      if (upcomingError) throw upcomingError;

      // Fetch past meetings
      const { data: past, error: pastError } = await supabase
        ?.from('meetings')
        ?.select(`
          *,
          organizer:user_profiles!created_by(full_name, email),
          team:teams(team_name)
        `)
        ?.or(`status.eq.completed,status.eq.cancelled,and(meeting_date.lt.${today}),and(meeting_date.eq.${today},meeting_time.lt.${currentTime})`)
        ?.order('meeting_date', { ascending: false })
        ?.order('meeting_time', { ascending: false });

      if (pastError) throw pastError;

      setUpcomingMeetings(upcoming || []);
      setPastMeetings(past || []);
    } catch (error) {
      console.error('Error fetching meetings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMeeting = () => {
    setShowCreateModal(true);
  };

  const handleMeetingCreated = () => {
    setShowCreateModal(false);
    fetchMeetings();
  };

  const handleViewDetails = (meeting) => {
    setSelectedMeeting(meeting);
    setShowDetailsModal(true);
  };

  const handleCancelMeeting = async (meetingId) => {
    try {
      const { error } = await supabase
        ?.from('meetings')
        ?.update({ status: 'cancelled' })
        ?.eq('id', meetingId);

      if (error) throw error;

      fetchMeetings();
    } catch (error) {
      console.error('Error cancelling meeting:', error);
      alert('Failed to cancel meeting');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString?.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      scheduled: 'bg-blue-100 text-blue-700',
      completed: 'bg-green-100 text-green-700',
      cancelled: 'bg-red-100 text-red-700',
      in_progress: 'bg-yellow-100 text-yellow-700'
    };
    return statusColors?.[status] || 'bg-gray-100 text-gray-700';
  };

  const getLocationTypeIcon = (locationType) => {
    switch (locationType) {
      case 'online':
        return 'Video';
      case 'in-person':
        return 'MapPin';
      case 'hybrid':
        return 'Users';
      default:
        return 'Calendar';
    }
  };

  const filteredUpcomingMeetings = upcomingMeetings?.filter(meeting => {
    const matchesSearch = meeting?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         meeting?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesFilter = filterType === 'all' || meeting?.location_type === filterType;
    return matchesSearch && matchesFilter;
  });

  const filteredPastMeetings = pastMeetings?.filter(meeting => {
    const matchesSearch = meeting?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         meeting?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesFilter = filterType === 'all' || meeting?.location_type === filterType;
    return matchesSearch && matchesFilter;
  });

  const MeetingCard = ({ meeting, isPast = false }) => (
    <div className="bg-card border border-border rounded-lg p-6 hover:shadow-lg transition-smooth">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-lg font-semibold text-foreground">{meeting?.title}</h3>
            <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusBadge(meeting?.status)}`}>
              {meeting?.status?.replace('_', ' ')?.toUpperCase()}
            </span>
          </div>
          <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
            {meeting?.description || 'No description provided'}
          </p>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Icon name="Calendar" size={16} />
          <span>{formatDate(meeting?.meeting_date)}</span>
          <Icon name="Clock" size={16} className="ml-2" />
          <span>{formatTime(meeting?.meeting_time)}</span>
          {meeting?.duration_minutes && (
            <span className="text-xs">({meeting?.duration_minutes} min)</span>
          )}
        </div>

        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Icon name={getLocationTypeIcon(meeting?.location_type)} size={16} />
          <span className="capitalize">{meeting?.location_type}</span>
          {meeting?.location_details && (
            <span className="text-xs text-muted-foreground">• {meeting?.location_details}</span>
          )}
        </div>

        {meeting?.organizer && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Icon name="User" size={16} />
            <span>Organized by {meeting?.organizer?.full_name}</span>
          </div>
        )}

        {meeting?.team && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Icon name="Users" size={16} />
            <span>{meeting?.team?.team_name}</span>
          </div>
        )}

        {meeting?.google_meet_link && !isPast && (
          <div className="flex items-center gap-2">
            <a
              href={meeting?.google_meet_link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 transition-micro"
            >
              <Icon name="Video" size={14} />
              Join Meeting
            </a>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 mt-4 pt-4 border-t border-border">
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleViewDetails(meeting)}
          iconName="Eye"
          iconPosition="left"
        >
          View Details
        </Button>
        {!isPast && meeting?.status === 'scheduled' && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleCancelMeeting(meeting?.id)}
            iconName="X"
            iconPosition="left"
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            Cancel
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <>
      <Helmet>
        <title>Meetings Management - PCF Admin Dashboard</title>
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="container mx-auto px-4 py-8 max-w-7xl">
          {/* Header Section */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Meetings Management</h1>
              <p className="text-muted-foreground">
                Schedule, manage, and track all platform meetings
              </p>
            </div>
            <Button
              variant="default"
              onClick={handleCreateMeeting}
              iconName="Plus"
              iconPosition="left"
              className="mt-4 md:mt-0"
            >
              Create Meeting
            </Button>
          </div>

          {/* Search and Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <Icon name="Search" size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search meetings..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="w-full pl-10 pr-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e?.target?.value)}
              className="px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">All Types</option>
              <option value="online">Online</option>
              <option value="in-person">In-Person</option>
              <option value="hybrid">Hybrid</option>
            </select>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Icon name="Loader2" size={32} className="animate-spin text-primary" />
            </div>
          ) : (
            <>
              {/* Upcoming Meetings Section */}
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <Icon name="CalendarDays" size={24} className="text-primary" />
                  <h2 className="text-2xl font-bold text-foreground">
                    Upcoming Meetings
                  </h2>
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 text-sm font-medium rounded-full">
                    {filteredUpcomingMeetings?.length}
                  </span>
                </div>
                
                {filteredUpcomingMeetings?.length === 0 ? (
                  <div className="bg-card border border-border rounded-lg p-8 text-center">
                    <Icon name="Calendar" size={48} className="mx-auto text-muted-foreground mb-3" />
                    <p className="text-muted-foreground">No upcoming meetings scheduled</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredUpcomingMeetings?.map((meeting) => (
                      <MeetingCard key={meeting?.id} meeting={meeting} />
                    ))}
                  </div>
                )}
              </div>

              {/* Past Meetings Section */}
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <Icon name="History" size={24} className="text-muted-foreground" />
                  <h2 className="text-2xl font-bold text-foreground">
                    Past Meetings
                  </h2>
                  <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm font-medium rounded-full">
                    {filteredPastMeetings?.length}
                  </span>
                </div>
                
                {filteredPastMeetings?.length === 0 ? (
                  <div className="bg-card border border-border rounded-lg p-8 text-center">
                    <Icon name="History" size={48} className="mx-auto text-muted-foreground mb-3" />
                    <p className="text-muted-foreground">No past meetings found</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredPastMeetings?.map((meeting) => (
                      <MeetingCard key={meeting?.id} meeting={meeting} isPast={true} />
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </main>
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateMeetingModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onMeetingCreated={handleMeetingCreated}
        />
      )}

      {showDetailsModal && selectedMeeting && (
        <MeetingDetailsModal
          isOpen={showDetailsModal}
          onClose={() => setShowDetailsModal(false)}
          meeting={selectedMeeting}
          onUpdate={fetchMeetings}
        />
      )}
    </>
  );
};

export default PCFAdminMeetingsPortal;